/**
 * Mathématiques de la simulation, sans dépendance au moteur de rendu.
 * Repère : droitier, Y haut, avant local = −Z, droite locale = +X.
 */

export class Vec3 {
  constructor(public x = 0, public y = 0, public z = 0) {}

  static from(a: readonly number[]): Vec3 {
    return new Vec3(a[0] ?? 0, a[1] ?? 0, a[2] ?? 0);
  }

  clone(): Vec3 {
    return new Vec3(this.x, this.y, this.z);
  }

  set(x: number, y: number, z: number): this {
    this.x = x; this.y = y; this.z = z;
    return this;
  }

  copy(v: Vec3): this {
    this.x = v.x; this.y = v.y; this.z = v.z;
    return this;
  }

  add(v: Vec3): Vec3 { return new Vec3(this.x + v.x, this.y + v.y, this.z + v.z); }
  sub(v: Vec3): Vec3 { return new Vec3(this.x - v.x, this.y - v.y, this.z - v.z); }
  scale(s: number): Vec3 { return new Vec3(this.x * s, this.y * s, this.z * s); }
  neg(): Vec3 { return new Vec3(-this.x, -this.y, -this.z); }

  addInPlace(v: Vec3): this { this.x += v.x; this.y += v.y; this.z += v.z; return this; }
  addScaledInPlace(v: Vec3, s: number): this { this.x += v.x * s; this.y += v.y * s; this.z += v.z * s; return this; }
  scaleInPlace(s: number): this { this.x *= s; this.y *= s; this.z *= s; return this; }

  dot(v: Vec3): number { return this.x * v.x + this.y * v.y + this.z * v.z; }

  cross(v: Vec3): Vec3 {
    return new Vec3(this.y * v.z - this.z * v.y, this.z * v.x - this.x * v.z, this.x * v.y - this.y * v.x);
  }

  lengthSq(): number { return this.x * this.x + this.y * this.y + this.z * this.z; }
  length(): number { return Math.sqrt(this.lengthSq()); }

  distanceTo(v: Vec3): number { return Math.hypot(this.x - v.x, this.y - v.y, this.z - v.z); }
  distanceSqTo(v: Vec3): number {
    const dx = this.x - v.x, dy = this.y - v.y, dz = this.z - v.z;
    return dx * dx + dy * dy + dz * dz;
  }

  /** Vecteur unitaire, ou `fallback` si la norme est quasi nulle. */
  normalized(fallback: Vec3 = ZERO): Vec3 {
    const len = this.length();
    return len > 1e-8 ? this.scale(1 / len) : fallback.clone();
  }

  /** Limite la norme à `max`. */
  clampedLength(max: number): Vec3 {
    const len = this.length();
    return len > max && len > 0 ? this.scale(max / len) : this.clone();
  }

  isNearlyZero(eps = 1e-6): boolean { return this.lengthSq() <= eps * eps; }

  lerp(v: Vec3, t: number): Vec3 {
    return new Vec3(this.x + (v.x - this.x) * t, this.y + (v.y - this.y) * t, this.z + (v.z - this.z) * t);
  }

  toArray(): [number, number, number] { return [this.x, this.y, this.z]; }
}

export const ZERO: Readonly<Vec3> = new Vec3();
export const UP = (): Vec3 => new Vec3(0, 1, 0);
export const FORWARD = (): Vec3 => new Vec3(0, 0, -1);
export const RIGHT = (): Vec3 => new Vec3(1, 0, 0);

export class Quat {
  constructor(public x = 0, public y = 0, public z = 0, public w = 1) {}

  static identity(): Quat { return new Quat(); }

  static from(a: readonly number[]): Quat {
    return new Quat(a[0] ?? 0, a[1] ?? 0, a[2] ?? 0, a[3] ?? 1).normalized();
  }

  static fromAxisAngle(axis: Vec3, angle: number): Quat {
    const n = axis.normalized();
    const s = Math.sin(angle / 2);
    return new Quat(n.x * s, n.y * s, n.z * s, Math.cos(angle / 2));
  }

  /** Quaternion à partir des axes locaux exprimés dans le monde (colonnes de la matrice). */
  static fromBasis(right: Vec3, up: Vec3, back: Vec3): Quat {
    const m00 = right.x, m10 = right.y, m20 = right.z;
    const m01 = up.x, m11 = up.y, m21 = up.z;
    const m02 = back.x, m12 = back.y, m22 = back.z;
    const trace = m00 + m11 + m22;
    let q: Quat;
    if (trace > 0) {
      const s = 0.5 / Math.sqrt(trace + 1);
      q = new Quat((m21 - m12) * s, (m02 - m20) * s, (m10 - m01) * s, 0.25 / s);
    } else if (m00 > m11 && m00 > m22) {
      const s = 2 * Math.sqrt(1 + m00 - m11 - m22);
      q = new Quat(0.25 * s, (m01 + m10) / s, (m02 + m20) / s, (m21 - m12) / s);
    } else if (m11 > m22) {
      const s = 2 * Math.sqrt(1 + m11 - m00 - m22);
      q = new Quat((m01 + m10) / s, 0.25 * s, (m12 + m21) / s, (m02 - m20) / s);
    } else {
      const s = 2 * Math.sqrt(1 + m22 - m00 - m11);
      q = new Quat((m02 + m20) / s, (m12 + m21) / s, 0.25 * s, (m10 - m01) / s);
    }
    return q.normalized();
  }

  /** Orientation dont l'avant (−Z) pointe vers `forward`, en gardant `up` comme référence (horizon stable). */
  static lookRotation(forward: Vec3, up: Vec3 = UP()): Quat {
    const f = forward.normalized(FORWARD());
    let right = f.cross(up);
    if (right.isNearlyZero(1e-4)) right = f.cross(new Vec3(1, 0, 0)).isNearlyZero(1e-4) ? new Vec3(0, 0, 1) : f.cross(new Vec3(1, 0, 0));
    right = right.normalized();
    const realUp = right.cross(f).normalized();
    return Quat.fromBasis(right, realUp, f.neg());
  }

  /** Rotation la plus courte entre deux directions unitaires. */
  static between(from: Vec3, to: Vec3): Quat {
    const a = from.normalized(), b = to.normalized();
    const d = a.dot(b);
    if (d >= 1 - 1e-9) return Quat.identity();
    if (d <= -1 + 1e-9) {
      let axis = new Vec3(1, 0, 0).cross(a);
      if (axis.isNearlyZero(1e-4)) axis = new Vec3(0, 1, 0).cross(a);
      return Quat.fromAxisAngle(axis, Math.PI);
    }
    const c = a.cross(b);
    return new Quat(c.x, c.y, c.z, 1 + d).normalized();
  }

  clone(): Quat { return new Quat(this.x, this.y, this.z, this.w); }

  copy(q: Quat): this { this.x = q.x; this.y = q.y; this.z = q.z; this.w = q.w; return this; }

  /** this × q (applique q dans le repère local de this). */
  multiply(q: Quat): Quat {
    return new Quat(
      this.w * q.x + this.x * q.w + this.y * q.z - this.z * q.y,
      this.w * q.y - this.x * q.z + this.y * q.w + this.z * q.x,
      this.w * q.z + this.x * q.y - this.y * q.x + this.z * q.w,
      this.w * q.w - this.x * q.x - this.y * q.y - this.z * q.z,
    );
  }

  conjugate(): Quat { return new Quat(-this.x, -this.y, -this.z, this.w); }

  normalized(): Quat {
    const len = Math.hypot(this.x, this.y, this.z, this.w);
    return len > 0 ? new Quat(this.x / len, this.y / len, this.z / len, this.w / len) : Quat.identity();
  }

  dot(q: Quat): number { return this.x * q.x + this.y * q.y + this.z * q.z + this.w * q.w; }

  rotate(v: Vec3): Vec3 {
    // v' = v + 2w(q×v) + 2 q×(q×v)
    const qx = this.x, qy = this.y, qz = this.z, qw = this.w;
    const tx = 2 * (qy * v.z - qz * v.y);
    const ty = 2 * (qz * v.x - qx * v.z);
    const tz = 2 * (qx * v.y - qy * v.x);
    return new Vec3(
      v.x + qw * tx + (qy * tz - qz * ty),
      v.y + qw * ty + (qz * tx - qx * tz),
      v.z + qw * tz + (qx * ty - qy * tx),
    );
  }

  forward(): Vec3 { return this.rotate(new Vec3(0, 0, -1)); }
  right(): Vec3 { return this.rotate(new Vec3(1, 0, 0)); }
  up(): Vec3 { return this.rotate(new Vec3(0, 1, 0)); }

  /** Angle entre deux orientations, en radians. */
  angleTo(q: Quat): number {
    const d = Math.min(1, Math.abs(this.dot(q)));
    return 2 * Math.acos(d);
  }

  slerp(q: Quat, t: number): Quat {
    let cos = this.dot(q);
    let target = q;
    if (cos < 0) { cos = -cos; target = new Quat(-q.x, -q.y, -q.z, -q.w); }
    if (cos > 0.9995) {
      return new Quat(
        this.x + (target.x - this.x) * t,
        this.y + (target.y - this.y) * t,
        this.z + (target.z - this.z) * t,
        this.w + (target.w - this.w) * t,
      ).normalized();
    }
    const theta = Math.acos(cos);
    const sin = Math.sin(theta);
    const a = Math.sin((1 - t) * theta) / sin;
    const b = Math.sin(t * theta) / sin;
    return new Quat(
      this.x * a + target.x * b,
      this.y * a + target.y * b,
      this.z * a + target.z * b,
      this.w * a + target.w * b,
    );
  }

  /** Vecteur rotation (axe × angle) de cette rotation, en radians. */
  toRotationVector(): Vec3 {
    let q: Quat = this.normalized();
    if (q.w < 0) q = new Quat(-q.x, -q.y, -q.z, -q.w);
    const sinHalf = Math.hypot(q.x, q.y, q.z);
    if (sinHalf < 1e-9) return new Vec3(q.x * 2, q.y * 2, q.z * 2);
    const angle = 2 * Math.atan2(sinHalf, q.w);
    return new Vec3(q.x, q.y, q.z).scale(angle / sinHalf);
  }

  toArray(): [number, number, number, number] { return [this.x, this.y, this.z, this.w]; }
}

export const DEG = Math.PI / 180;
export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** Équivalent de FMath::FInterpTo. */
export function interpTo(current: number, target: number, dt: number, speed: number): number {
  if (speed <= 0) return target;
  const dist = target - current;
  if (dist * dist < 1e-8) return target;
  return current + dist * clamp(dt * speed, 0, 1);
}
/** Équivalent de FMath::InterpEaseIn. */
export const easeIn = (a: number, b: number, t: number, exp: number) => lerp(a, b, Math.pow(t, exp));

/** Fait tourner `dir` d'un angle autour de `axis`. */
export function rotateAround(dir: Vec3, axis: Vec3, angle: number): Vec3 {
  return Quat.fromAxisAngle(axis, angle).rotate(dir);
}

/** Intersection segment–sphère : fraction t ∈ [0,1] du premier contact, ou null. */
export function segmentSphere(start: Vec3, end: Vec3, center: Vec3, radius: number): number | null {
  const d = end.sub(start);
  const f = start.sub(center);
  const a = d.dot(d);
  const c = f.dot(f) - radius * radius;
  if (c <= 0) return 0;
  if (a < 1e-12) return null;
  const b = 2 * f.dot(d);
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  return t >= 0 && t <= 1 ? t : null;
}
